/**
 * RouteGuard — Route-level RBAC guard following SkySign's pattern.
 *
 * Single source of truth: if a nav item is visible in the sidebar for this user,
 * the corresponding route is accessible. If not visible → redirect to home.
 *
 * This replaces the old PermissionGuard + ROUTE_PERMISSIONS approach where
 * permissions were hardcoded per-route and could get out of sync with the sidebar.
 */
import { type ReactNode, useMemo } from "react";
import { Navigate, useLocation } from "react-router";
import { useSelector } from "react-redux";
import type { NavSection } from "@skypointcloud/skypoint-ui";
import { buildNavSections } from "../views/dashboard/sidebar/sidebarConfig";
import { getRequiredNavId } from "../views/dashboard/sidebar/routeMapping";
import { useGetMyPermissionsQuery } from "../store/api/rbacApi";
import type { IRootReducerState } from "../store/IRootReducer";

function collectNavIds(sections: NavSection[]): Set<string> {
  const ids = new Set<string>();
  for (const section of sections) {
    for (const item of section.items) {
      ids.add(item.id);
      if (item.children) {
        for (const child of item.children) {
          ids.add(child.id);
        }
      }
    }
  }
  return ids;
}

interface RouteGuardProps {
  children: ReactNode;
}

export function RouteGuard({ children }: RouteGuardProps) {
  const location = useLocation();
  const authState = useSelector((state: IRootReducerState) => state.authReducer);
  const { isPlatformAdmin, isTenantAdmin, isInstanceAdmin } = authState.permissions;
  const isFullAccess = authState.isFullAccess;
  const accessToken = authState.accessToken;

  const { data: rbacPermissions, isLoading } = useGetMyPermissionsQuery(undefined, {
    skip: !accessToken,
  });

  const visibleNavIds = useMemo(() => {
    const sections = buildNavSections({
      isFullAccess,
      isTenantAdmin: isTenantAdmin ?? false,
      isPlatformAdmin: isPlatformAdmin ?? false,
      isInstanceAdmin: isInstanceAdmin ?? false,
      rbacNavigation: rbacPermissions?.navigation,
    });
    return collectNavIds(sections);
  }, [isFullAccess, isTenantAdmin, isPlatformAdmin, isInstanceAdmin, rbacPermissions?.navigation]);

  const requiredNavId = getRequiredNavId(location.pathname);

  // Route not mapped to any nav item — always allow (Dashboard, Account, public pages, etc.)
  if (requiredNavId === null) {
    return <>{children}</>;
  }

  // Permissions still loading — allow through (sidebar is also loading at this point)
  if (isLoading) {
    return <>{children}</>;
  }

  // Nav item not visible for this user → redirect to home
  if (!visibleNavIds.has(requiredNavId)) {
    return (
      <Navigate
        to="/"
        replace
      />
    );
  }

  return <>{children}</>;
}
